import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { sidebarColors } from '../sidebarStyles';

// Topbar clock: one card split into a TIME segment (big HH:MM, with seconds
// and AM/PM set small beside it) and a DATE segment (weekday over the date).
// Same height, radius and surface as the theme toggle / bell next to it, so
// the topbar reads as one row of controls. Phones show the time only.
const DateTimeDisplay = ({ theme, language, isMobile, isTablet }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // No room for it on very small screens.
  if (isMobile && window.innerWidth < 640) return null;

  const isAr = language === 'ar';
  const isDark = theme === 'dark';
  const locale = isAr ? 'ar-EG' : 'en-US';
  const c = sidebarColors[theme] || sidebarColors.light;
  const accent = isDark ? '#60a5fa' : '#2e7cba';

  const timeParts = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).formatToParts(now);
  const part = (type) => timeParts.find((p) => p.type === type)?.value || '';

  const hoursMinutes = `${part('hour')}:${part('minute')}`;
  const seconds = part('second');
  const dayPeriod = part('dayPeriod');
  const weekday = now.toLocaleDateString(locale, { weekday: 'long' });
  const date = now.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  const fullLabel = now.toLocaleString(locale, { dateStyle: 'full', timeStyle: 'medium', hour12: true });

  const muted = { color: c.textSecondary, fontWeight: 600 };

  return (
    <div
      title={fullLabel}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '0.5rem' : '0.75rem',
        height: isMobile ? '2.375rem' : '2.5rem',
        paddingBlock: 0,
        paddingInlineStart: '0.3125rem',
        paddingInlineEnd: isMobile ? '0.625rem' : '0.875rem',
        boxSizing: 'border-box',
        background: c.inputBg,
        border: `1px solid ${c.inputBorder}`,
        borderRadius: '0.625rem',
        color: c.text,
        whiteSpace: 'nowrap',
        userSelect: 'none',
        cursor: 'default',
      }}
    >
      {/* ── Time ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span
          style={{
            width: '1.75rem',
            height: '1.75rem',
            borderRadius: '0.5rem',
            background: c.accentSoft,
            color: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Clock size={15} />
        </span>

        {/* Clock digits always run left-to-right, even in Arabic. Tabular
            figures keep the width steady as the seconds tick. */}
        <span
          style={{
            display: 'flex',
            alignItems: 'baseline',
            direction: 'ltr',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
          }}
        >
          <span
            style={{
              fontSize: isMobile ? '0.9375rem' : '1rem',
              fontWeight: 700,
              letterSpacing: '0.01em',
            }}
          >
            {hoursMinutes}
          </span>
          {!isMobile && (
            <span style={{ ...muted, fontSize: '0.75rem', marginInlineStart: '0.125rem' }}>
              :{seconds}
            </span>
          )}
          {dayPeriod && (
            <span
              style={{
                ...muted,
                fontSize: '0.6875rem',
                fontWeight: 700,
                marginInlineStart: '0.3125rem',
                textTransform: 'uppercase',
              }}
            >
              {dayPeriod}
            </span>
          )}
        </span>
      </div>

      {/* ── Date ── */}
      {!isMobile && (
        <>
          <span
            aria-hidden="true"
            style={{ width: '1px', height: '1.375rem', background: c.inputBorder, flexShrink: 0 }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', lineHeight: 1.1 }}>
            <span style={{ fontSize: '0.6875rem', fontWeight: 500, color: c.textSecondary }}>
              {weekday}
            </span>
            <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>
              {date}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default DateTimeDisplay;
